import { useEffect } from "react";
import { Link } from "react-router-dom";
import LegalLayout from "@/components/LegalLayout";

export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy Policy · Stable-ish";
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalLayout
      title="Privacy Policy"
      eyebrow="§ legal · privacy"
      lastUpdated="Effective: May 26, 2026 · Last updated: July 26, 2026"
    >
      <p>
        Stable-ish is a clinical knowledge reference tool for trained emergency
        department and critical care registered nurses. It is operated by{" "}
        <strong>Stable-ish LLC</strong> (&quot;we&quot;, &quot;us&quot;,
        &quot;the app&quot;). We respect your privacy. This policy explains what
        data the app collects, how it is used, and your choices.
      </p>

      <div className="callout">
        <strong>Important:</strong> Stable-ish is <strong>not</strong> a
        HIPAA-covered service.{" "}
        <strong>Do not enter Protected Health Information (PHI)</strong> such as
        patient names, MRNs, dates of birth, or any data that could identify a
        specific patient. Questions should be phrased generically (e.g.,{" "}
        &quot;70 kg adult on norepi&quot; — not &quot;Mr. Smith room 4&quot;).
      </div>

      <h2>1. Who we are</h2>
      <p>
        Stable-ish is built and operated by the Stable-ish team. For privacy
        questions, contact us at{" "}
        <a href="mailto:privacy@stableish.me">privacy@stableish.me</a>.
      </p>

      <h2>2. What information we collect</h2>
      <p>We collect the minimum data needed to operate the app:</p>
      <ul>
        <li>
          <strong>Anonymous profile ID.</strong> When you first open the app, a
          random UUID (e.g., <span className="mono">a1b2c3...</span>) is generated
          and stored locally on your device. We do not collect your name, email,
          phone number, or any contact information. No account or login is
          required.
        </li>
        <li>
          <strong>Clinical questions and AI responses.</strong> Questions you
          type or dictate, and the answers returned, are stored on our backend
          tied to your anonymous profile ID so you can see your history and
          bookmarks. They are <strong>not</strong> associated with any
          identifiable person.
        </li>
        <li>
          <strong>Bookmarks and saved items.</strong> Tool snippets, scenarios,
          and questions you save.
        </li>
        <li>
          <strong>Audit logs.</strong> Endpoint, timestamp, latency, and
          anonymous profile ID are logged for safety review and quality
          improvement.
        </li>
        <li>
          <strong>Camera, microphone, photo library access.</strong> Used only
          when you explicitly tap a feature that requires them (ECG photo
          upload, voice dictation, document attachment). The image or audio is
          sent transiently to our AI provider for processing and the result is
          stored — we do not store the raw image or audio file beyond the
          request.
        </li>
        <li>
          <strong>Clinical calculator inputs.</strong> When you use a bedside
          calculator (weight-based dose, Parkland burn fluid, Peds dose,
          GCS, CrCl, anion gap, IBW/ABW, etc.), you may type numeric values
          such as a weight, a lab value, or vital signs. These values are
          used only to compute the calculator result. They are{" "}
          <strong>not</strong> associated with any identifiable patient and
          are <strong>not</strong> transmitted to product-analytics providers
          — PostHog receives only the fact that a given calculator was
          opened, never the numeric values you entered.
        </li>
        <li>
          <strong>Subscription status.</strong> If you start a free trial or
          subscribe, we store whether you have an active subscription and
          which tier (monthly / annual / founding member). We do{" "}
          <strong>not</strong> see or store your credit card, bank details,
          or Apple ID password — Apple handles all payment processing.
        </li>
      </ul>

      <h2>3. Information we do NOT collect</h2>
      <ul>
        <li>Your name, email, phone number, address, or any contact info</li>
        <li>Location (the app does not request GPS access)</li>
        <li>Health data from Apple Health / HealthKit</li>
        <li>Contacts or calendar</li>
        <li>Advertising identifiers (IDFA) — we do not show ads</li>
        <li>Patient data, MRNs, or any field that identifies a specific patient</li>
      </ul>

      <h2>4. How we use your information</h2>
      <ul>
        <li>To generate AI clinical reference responses</li>
        <li>To show your question history and saved bookmarks</li>
        <li>To improve answer quality, detect bugs, and review safety</li>
        <li>To comply with legal obligations if applicable</li>
      </ul>
      <p>
        We do <strong>not</strong> sell, rent, or share your data with
        advertisers.
      </p>

      <h2>5. Third-party AI providers</h2>
      <p>
        To generate clinical reference content, the app sends your typed or
        dictated question to AI providers under contract:
      </p>
      <ul>
        <li>
          <strong>Anthropic (Claude Sonnet 4.5)</strong> — text questions and AI
          responses (
          <a
            href="https://www.anthropic.com/legal/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            privacy policy
          </a>
          ). Inputs sent to Claude are not used to train Anthropic models per
          their API terms.
        </li>
        <li>
          <strong>OpenAI (Whisper)</strong> — audio you record for voice
          dictation (
          <a
            href="https://openai.com/policies/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            privacy policy
          </a>
          ). Audio is transcribed and discarded.
        </li>
      </ul>
      <p>
        These providers process content strictly to deliver the response and do
        not retain it beyond their stated retention windows.
      </p>

      <h2>5b. Website analytics &amp; advertising tags</h2>
      <p>
        Our marketing website (<strong>stableish.me</strong>) uses the following
        third-party tags to measure aggregate visitor behaviour and to attribute
        conversions from paid campaigns:
      </p>
      <ul>
        <li>
          <strong>Google Analytics 4</strong> — page views, referral sources,
          country/region (not precise location), device type.
        </li>
        <li>
          <strong>Google Ads conversion tracking</strong> — records
          &quot;App Store click&quot; events so we can measure the return on
          paid ad campaigns.
        </li>
        <li>
          <strong>Meta Pixel</strong> — same purpose as Google Ads tracking,
          for Facebook and Instagram ad campaigns.
        </li>
        <li>
          <strong>TikTok Pixel</strong> — attribution and conversion tracking
          for TikTok ad campaigns. Records aggregate pageviews and App Store
          click events. Data sent to TikTok Ads (US). Gated by our cookie
          banner and GPC signal in the same way as Meta and Google Ads.
        </li>
        <li>
          <strong>PostHog (marketing site)</strong> — anonymous product
          analytics for the marketing website only. Records aggregate pageviews,
          autocaptured clicks (e.g., which App Store button you tapped), and
          referrer / campaign source. Session replays are{" "}
          <strong>disabled</strong> on the marketing site. PostHog receives an
          anonymous random ID assigned to your browser session and coarse
          geolocation derived server-side from your IP; we do not send PostHog
          your email, name, or any personal identifier. Data is sent to
          PostHog Cloud (US).
        </li>
        <li>
          <strong>Emergent platform analytics</strong> — our hosting provider
          (Emergent) also runs its own operational telemetry to keep the site
          online and diagnose errors. This is separate from our PostHog
          instance and is not tied to any personal identifier.
        </li>
      </ul>
      <p>
        All of the above are gated by <strong>Google Consent Mode v2</strong>{" "}
        and our cookie banner. Nothing that would set an identifying cookie
        fires until you tap <strong>&ldquo;Accept&rdquo;</strong>. If you tap{" "}
        <strong>&ldquo;Reject&rdquo;</strong> (or ignore the banner), Google,
        Meta, TikTok, and PostHog are all opted out — no persistent identifier
        is stored on your device. We also honour the Global Privacy Control
        (GPC) browser signal as an automatic opt-out.
      </p>
      <p>
        Website analytics are entirely separate from any data collected by the
        Stable-ish iOS app.
      </p>

      <h2>5c. Mobile app analytics (iOS)</h2>
      <p>
        Inside the Stable-ish iOS app, we use two lightweight tools to
        understand how nurses use the app and to catch bugs before they affect
        more users:
      </p>
      <ul>
        <li>
          <strong>PostHog</strong> — product analytics. Records anonymous
          events like &ldquo;opened the ACLS scenario&rdquo; or &ldquo;used the
          drip calculator&rdquo; so we can improve what nurses actually reach
          for. We do <strong>not</strong> send PostHog your email, username,
          real name, or your device&rsquo;s user-assigned name. PostHog does,
          however, receive:
          <ul>
            <li>
              A random anonymous ID assigned to your install (not linked to
              your identity).
            </li>
            <li>
              Your device model &amp; iOS version (e.g., iPhone 15 Pro Max,
              iOS 26).
            </li>
            <li>
              Approximate location derived from your IP address — city, state,
              postal code, and coarse latitude/longitude. Your raw IP address
              is processed server-side by PostHog only to derive this location
              and is not stored as a queryable property.
            </li>
          </ul>
        </li>
        <li>
          <strong>Sentry</strong> — crash and error reporting. When something
          in the app throws an error, Sentry captures the technical details so
          we can fix it. What Sentry receives:
          <ul>
            <li>
              A random pseudonymous user ID (UUID like{" "}
              <code>9279c02d-ec3c-4322-8f29-771993558a18</code>) — not linked
              to your email or name.
            </li>
            <li>Device model (e.g., iPhone 15 Pro Max) and iOS version.</li>
            <li>The stack trace and code path that caused the error.</li>
          </ul>
          Sentry does <strong>not</strong> receive your email, real name, IP
          address, or any content you type into the app.
        </li>
      </ul>
      <p>
        Sentry is also used on our backend server to catch API errors. That
        usage is server-side only and never involves a device identifier.
      </p>

      <h2>5d. Subscription payments (Apple IAP + RevenueCat)</h2>
      <p>
        Stable-ish offers a paid subscription (14-day free trial, then
        $9.99/month or $79.99/year) via Apple&rsquo;s In-App Purchase system.
        When you subscribe:
      </p>
      <ul>
        <li>
          <strong>Apple</strong> processes the payment. Stable-ish LLC never
          sees your credit card, bank account, or Apple ID password.
        </li>
        <li>
          <strong>RevenueCat</strong> — a subscription-management service —
          receives the anonymous transaction receipt from Apple so that we
          can verify your subscription status across devices, handle
          restore-purchase requests, and grant Founding Member pricing to
          existing subscribers. RevenueCat receives your anonymous app user
          ID, subscription tier, trial status, and purchase timestamps.
          It does <strong>not</strong> receive your name, email, credit card,
          or any clinical content.
        </li>
      </ul>
      <p>
        To manage or cancel your subscription, go to{" "}
        <strong>Settings → Apple ID → Subscriptions</strong> on your iPhone —
        we do not have the ability to cancel it for you, but our support team
        will point you to the right menu at any time.
      </p>

      <h2>5e. California residents (CCPA / CPRA)</h2>
      <p>
        We <strong>do not sell or share</strong> personal information for
        cross-context behavioural advertising as those terms are defined under
        the California Consumer Privacy Act. If you are a California resident
        and want to exercise your rights to access, correct, or delete personal
        information, email{" "}
        <a href="mailto:privacy@stableish.me">privacy@stableish.me</a>. You may
        also revoke your analytics consent at any time by clearing your browser
        storage for stableish.me, which will bring the cookie banner back on
        your next visit.
      </p>

      <h2>6. Data retention and deletion</h2>
      <p>
        Your data is associated with your anonymous profile ID. You can delete
        your data at any time:
      </p>
      <ul>
        <li>
          Open the app → <strong>Settings</strong> → &quot;Clear all data&quot;
        </li>
        <li>
          Or email{" "}
          <a href="mailto:privacy@stableish.me">privacy@stableish.me</a> with
          your profile ID and we will purge it
        </li>
        <li>
          Uninstalling the app removes the local profile ID; without it, your
          backend data becomes unrecoverable orphaned content
        </li>
      </ul>

      <h2>7. Security</h2>
      <ul>
        <li>All traffic uses HTTPS (TLS) in transit</li>
        <li>
          Data is stored on managed cloud infrastructure with industry-standard
          access controls
        </li>
        <li>We do not collect identifiers that could be used to re-identify you</li>
      </ul>
      <p>No system is 100% secure. By using Stable-ish you accept residual risk.</p>

      <h2>8. Minors</h2>
      <p>
        Stable-ish is intended for licensed registered nurses and clinicians
        only. It is not directed to minors under 17. We do not knowingly
        collect data from minors.
      </p>

      <h2>9. International users</h2>
      <p>
        The app is operated from the United States. By using Stable-ish you
        consent to processing of your information in the United States.
      </p>

      <h2>10. Your rights</h2>
      <p>
        Depending on where you live (e.g., California CCPA, EU GDPR), you may
        have the right to:
      </p>
      <ul>
        <li>Request a copy of the data we hold about your profile ID</li>
        <li>Request deletion of that data</li>
        <li>Object to specific processing</li>
      </ul>
      <p>
        Email <a href="mailto:privacy@stableish.me">privacy@stableish.me</a>{" "}
        with your anonymous profile ID (found in Settings) to make a request.
      </p>

      <h2>11. Medical disclaimer</h2>
      <p>
        Stable-ish is an educational reference tool. AI-generated content can be
        incomplete, outdated, or wrong. Stable-ish does <strong>not</strong>{" "}
        diagnose, treat, or prescribe. Always verify against your hospital
        policies, pharmacy, and provider orders. Do not use Stable-ish in place
        of clinical judgment.
      </p>

      <h2>12. Changes</h2>
      <p>
        We may update this policy from time to time. Material changes will be
        announced in the app. The &quot;Last updated&quot; date at the top will
        always reflect the current revision.
      </p>

      <h2>13. Contact</h2>
      <p>
        Privacy questions:{" "}
        <a href="mailto:privacy@stableish.me">privacy@stableish.me</a>
        <br />
        Safety concerns:{" "}
        <a href="mailto:safety@stableish.me">safety@stableish.me</a>
      </p>
      <p>
        <strong>Mailing address:</strong>
        <br />
        Stable-ish LLC
        <br />
        557 Prospect Ave., Suite 102
        <br />
        West Hartford, CT 06105
        <br />
        United States
      </p>

      <h2>14. Related Policies</h2>
      <p>
        This Privacy Policy should be read alongside our{" "}
        <Link to="/terms" className="text-[#FF2A2A]">Terms of Service</Link>{" "}
        and{" "}
        <Link to="/medical-disclaimer" className="text-[#FF2A2A]">Medical Disclaimer</Link>,
        which together govern your use of Stable-ish.
      </p>
    </LegalLayout>
  );
}
